import React, { useCallback, useState } from 'react';

import { di } from 'react-magnetic-di';
import type { useMutation, UseMutationConfig } from 'react-relay';
import type { Disposable, MutationParameters } from 'relay-runtime';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import {
	getTraceIds,
	startCapturingTraceIds,
	stopCapturingTraceIds,
} from '@atlassian/relay-traceid';

import { logException } from '../sentry';

type ExtraParams = {
	interactionName: string;
	/**
	 * Use the generated operation name from the graphql file
	 */
	operationName: string;
};

/**
 * Wraps a relay mutation hook and adds UFO hold and error handling.
 * ALWAYS render the `renderUFOHold` when consuming this hook for the reporting to be correct.
 */
export const useMutationWithUFO = <T extends MutationParameters>(
	useMutationHook: ReturnType<typeof useMutation<T>>,
	{ interactionName, operationName }: ExtraParams,
): {
	commitMutation: (config: UseMutationConfig<T>) => Disposable;
	isError: boolean;
	isMutating: boolean;
	renderUFOHold: () => React.ReactNode;
} => {
	di();
	const [commitMutation, isMutating] = useMutationHook;
	const [isError, setIsError] = useState(false);

	const handleMutation = (config: UseMutationConfig<T>) => {
		startCapturingTraceIds(operationName);
		setIsError(false);
		traceUFOPress(interactionName);

		const logError = <T extends { message: string }>(error: Error | T) => {
			const traceIds = getTraceIds(operationName);
			logException(error instanceof Error ? error : new Error(error.message), {
				teamName: 'rovo-agents-fe',
				loggerName: `UFOMutationError-${interactionName}`,
				...error,
				traceId: traceIds?.[0] ?? 'trace-id-not-found',
			});
		};

		return commitMutation({
			...config,
			onError: (error) => {
				logError(error);
				setInteractionError(interactionName, {
					errorMessage: error.message,
					name: error.name,
				});

				setIsError(true);
				config.onError?.(error);
				stopCapturingTraceIds(operationName);
			},
			onCompleted: (response, errors) => {
				if (errors?.length) {
					const error = errors[0];
					logError(error);
					setInteractionError(interactionName, {
						errorMessage: error.message,
						name: error.message,
					});
					setIsError(true);
				} else {
					// Check for errors in mutation response and set interaction error if present.
					// Example response:
					// {
					// 	agentStudio_updateScenario: {
					// 		errors: [{ message: 'Error' }]
					// 	}
					// }
					for (const value of Object.values(response)) {
						if (
							!!value &&
							typeof value === 'object' &&
							'errors' in value &&
							Array.isArray(value.errors) &&
							value.errors.length
						) {
							const error = value.errors[0];
							logError(error);
							setInteractionError(interactionName, {
								errorMessage: error.message,
								name: error.message,
							});
							setIsError(true);
							break;
						}
					}
				}

				config.onCompleted?.(response, errors);
				stopCapturingTraceIds(operationName);
			},
		});
	};

	const renderUFOHold = useCallback(
		() => <UFOLoadHold hold={isMutating} name={interactionName} />,
		[isMutating, interactionName],
	);

	return {
		commitMutation: handleMutation,
		isMutating,
		isError,
		renderUFOHold,
	};
};
